import Component from '@glimmer/component'
import { action } from '@ember/object'
import { isEmpty, isPresent } from '@ember/utils'
import { service } from '@ember/service'
import { task, timeout } from 'ember-concurrency'
import { htmlSafe, type SafeString } from '@ember/template'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import {
  tableColumnsToComparerKeyMapping,
  injectComparerKeys,
  type SortingConfig,
  type SortComparerKey,
} from '@blakeelearning/data-tables/utils/sorting'
import type { IntlService } from 'ember-intl'
import type DataTransformerService from '@blakeelearning/data-tables/services/data-transformer'
import { tracked } from '@glimmer/tracking'
import type { CustomPipelineDescriptor, PipelineDescriptor } from '@blakeelearning/data-tables/utils/data-transformer'
import type { PaginationConfig } from '@blakeelearning/data-tables/utils/paging'
import applyTransform from '@blakeelearning/data-tables/modifiers/apply-transform'
import { t } from 'ember-intl'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'
import SortableCell from 'teacher-ui-client/components/stable-table/basic-cells/sortable'
import ClickableCell from 'teacher-ui-client/components/stable-table/basic-cells/clickable'
import { StarLegend } from 'teacher-ui-client/components/tables/fluency-sprints/star-legend'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { hash, array, fn, concat } from '@ember/helper'
import { and, not, eq } from 'ember-truth-helpers'
import { TabGroupComponent } from 'teacher-ui-client/components/tab-group'
import { get } from '@ember/object'
import { on } from '@ember/modifier'
import { TAB_TYPE_KEYS } from 'teacher-ui-client/components/tab-group/tab-types'
import type { RowObject } from 'teacher-ui-client/components/stable-table/basic-table/cell'
import type RouterService from '@ember/routing/router-service'
import type { MentalMinuteStudentLessonItem } from 'teacher-ui-client/utils/student-events/fetchers/mental-minute-student'

interface Args {
  studentRouteName: string
  onCategoryClick?: ({ category }: { category?: string }) => void
  onSortClick?: ({ sortKey }: SortObject) => void
  allLessons?: LessonItem[]
  sortingConfig?: SortingConfig
  students?: StudentItem[]
  paginationConfig?: PaginationConfig
  category?: string
  exportable?: boolean
}

interface Signature {
  Args: Args
}

export interface LessonItem {
  lessonId: number
  lessonName: string
  category: string
  categoryName: string
  stars?: number
}

export interface StudentItem extends RowObject {
  studentId: number
  firstName: string
  lastName: string
  externalId: Nullable<string>
  highestLessonPassed: { label: string; id: number; name: string }
  lessons: MentalMinuteStudentLessonItem[]
}

interface StudentItemWithScores extends StudentItem {
  lessonScores: {
    [key: string]: LessonItem
  }
}

interface StudentColumn {
  value?: string | SafeString
  cellClassNames?: string
  valuePath?: string
  sortKey?: string
  comparerKey?: SortComparerKey
  classNames?: string
  component?: typeof SortableCell
}

interface LessonColumn {
  value?: { id: number; name: string; category: string }
  lessonId?: number
  cellClassNames?: string
  valuePath?: string
  sortKey?: string
  comparerKey?: SortComparerKey
  classNames?: string
}

export class FluencySprintsStudentResults extends Component<Signature> {
  @service router!: RouterService
  @service intl!: IntlService
  @service dataTransformer!: DataTransformerService

  @tracked transformedItems: StudentItemWithScores[] = []
  @tracked unpaginatedItems: StudentItemWithScores[] = []
  @tracked pageCount = 1

  get allLessons() {
    return this.args.allLessons ?? []
  }

  get categoryThemeMapping() {
    return {
      all: ThemeKeys.green,
      addition: ThemeKeys.blue,
      subtraction: ThemeKeys.orange,
      multiplication: ThemeKeys.green,
      division: ThemeKeys.purple,
    }
  }

  /**
   * Returns an array of operation descriptions that will be applied to the items
   */
  get pipelineDescriptors() {
    return [this.rowDescriptor, this.sortingDescriptor, this.paginationDescriptor]
  }

  get rowDescriptor(): CustomPipelineDescriptor {
    const transformer = ({ items, ...rest }: { items: StudentItem[] }): { items: StudentItemWithScores[] } => {
      return {
        ...rest,
        items: items.map((student) => {
          // Map to object with lesson id keys
          const lessonScores = student.lessons.reduce<StudentItemWithScores['lessonScores']>(
            (lessons, studentLesson) => {
              const { best_score: bestScore, stars } = studentLesson
              return { ...lessons, [studentLesson.lesson]: { bestScore, stars } }
            },
            {},
          )

          return { ...student, lessonScores }
        }),
      }
    }

    return { type: 'custom', transformer }
  }

  get sortingDescriptor() {
    const tableColumns = this.studentColumns

    if (!this.args.sortingConfig) return null

    const comparerKeyMapping = tableColumnsToComparerKeyMapping(tableColumns)
    const sortingConfigWithComparerKeys = injectComparerKeys(this.args.sortingConfig, comparerKeyMapping)
    const sortDescriptor = { type: 'sort', config: sortingConfigWithComparerKeys }

    return sortDescriptor
  }

  get paginationDescriptor() {
    if (!this.args.paginationConfig) return null
    return { type: 'paginate', config: this.args.paginationConfig }
  }

  /**
   * This task provides an array of items and operations to the data transformer service, which will asynchronously
   * transform the data according to the operations, then set those transformed items for display in the table
   */
  @action
  performTransformItems(_element: unknown, [items, pipelineDescriptors]: [Args['students'], PipelineDescriptor[]]) {
    void this.transformItems.perform(items, pipelineDescriptors)
  }

  transformItems = task(
    { restartable: true },
    async (items: Args['students'] = [], pipelineDescriptors: PipelineDescriptor[] = []) => {
      await timeout(200) // debounce

      const presentDescriptors = pipelineDescriptors.filter(isPresent)
      const transformedData = await this.dataTransformer.buildAndTransform<StudentItem, StudentItemWithScores>(
        items,
        presentDescriptors,
      )

      this.pageCount = transformedData.pageCount
      this.transformedItems = transformedData.items
      this.unpaginatedItems = transformedData.paginated?.flat() ?? []
    },
  )

  /**
   * This computed uses the category (if provided) to filter all available lessons to just the selected category. This
   * is separate to the data items transformation above, as the category is used here to filter the table columns (lessons), rather than rows.
   */
  get categoryLessons() {
    if (isEmpty(this.args.category)) {
      return this.allLessons
    } else {
      return this.allLessons.filter((lesson) => lesson.category === this.args.category)
    }
  }

  get defaultColumn(): StudentColumn {
    return {
      classNames: 'table-box table-box-header bg-ms-green-100',
      cellClassNames: 'table-box table-box-cell bg-ms-green-50 group-hover:bg-ms-green-100',
      component: SortableCell,
      comparerKey: 'sortByValue',
    }
  }

  get studentColumns(): StudentColumn[] {
    const { intl, defaultColumn } = this
    const headerTranslationPrefix = 'tables.fluencySprints.studentResults.headers'

    const columns: StudentColumn[] = [
      { ...defaultColumn, value: intl.t(`${headerTranslationPrefix}.firstName`), valuePath: 'firstName' },
      { ...defaultColumn, value: intl.t(`${headerTranslationPrefix}.lastName`), valuePath: 'lastName' },
      {
        ...defaultColumn,
        value: htmlSafe(intl.t(`${headerTranslationPrefix}.highestPassed`)),
        valuePath: 'highestLessonPassed.label',
        sortKey: 'highestLessonPassed.id',
        comparerKey: 'sortByNumber',
      },
    ]
    return columns
  }

  get exportStudentColumns(): StudentColumn[] {
    const { intl, defaultColumn, studentColumns } = this
    const [firstName, lastName, ...rest] = studentColumns

    return [
      firstName,
      lastName,
      {
        ...defaultColumn,
        value: intl.t('reporting.dashboardTables.studentExternalId'),
        valuePath: 'externalId',
      },
      ...rest,
    ]
  }

  get lessonColumns(): LessonColumn[] {
    const defaultColumn = {
      classNames: `table-box table-box-header lessons-title-header-cell`,
      cellClassNames: 'table-box table-box-cell bg-dusty-black-50 text-center',
      formatEmptyConfig: { replacement: '' },
    }

    const lessonColumns: LessonColumn[] = this.categoryLessons.map((lesson) => {
      return {
        ...defaultColumn,
        value: { id: lesson.lessonId, name: lesson.lessonName, category: lesson.category },
        valuePath: `lessonScores.${lesson.lessonId}.bestScore`,
        // ensure we still place text in the cell for empty scores, so that the line height matches
        formatEmptyConfig: { replacement: htmlSafe('&nbsp;') },
        lessonId: lesson.lessonId,
      }
    })

    // If there are lesson columns to display, add 2 empty columns to the end, giving room for diagonal text of the
    // final lesson column header, rather than clipping it
    if (lessonColumns && lessonColumns.length) {
      lessonColumns.push(defaultColumn, defaultColumn)
    }
    return lessonColumns
  }

  get studentRows() {
    const { router, args } = this
    return this.transformedItems.map((student) => {
      const nameCell = {
        component: ClickableCell,
        componentClassNames: 'cursor-pointer text-oceany-blue-325 font-bold',
        onClick() {
          void router.transitionTo(args.studentRouteName, student.studentId)
        },
      }

      return {
        ...student,
        classNames: 'table-row-striped group',
        cells: [{ ...nameCell }, { ...nameCell }, {}],
      }
    })
  }

  get lessonRows() {
    return this.transformedItems.map((student) => {
      const lessonCells = this.lessonColumns.map((lessonColumn) => {
        const lessonColumnId = lessonColumn.lessonId
        if (lessonColumnId) {
          const lessonScore = student.lessonScores[lessonColumnId]
          const stars = lessonScore?.stars
          if (stars !== undefined) {
            return { classNames: `bg-star-${stars}` }
          }
        }
        return {}
      })
      return { ...student, cells: lessonCells }
    })
  }

  onTabClick = ({ category = undefined }: { category?: string }) => {
    this.args.onCategoryClick?.({ category })

    document.querySelector('#results-tab-content')?.scrollTo(0, 0)
  }

  <template>
    <div
      class="__tables__fluency-sprints__student-results__3cd5d fixed-table"
      {{applyTransform this.performTransformItems @students this.pipelineDescriptors}}
    >
      <section data-test-students class="fixed-table-sidebar">
        <header class="fixed-table-sidebar-header mt-3 pl-3">
          <p class="mb-3">
            <strong>{{t "tables.fluencySprints.studentResults.headers.mentalMinute"}}</strong><br />
            {{t "tables.fluencySprints.studentResults.headers.skillsMastered"}}
          </p>
        </header>
        <BasicTable
          class="relative w-full"
          @columns={{this.studentColumns}}
          @rows={{this.studentRows}}
          @config={{hash sortingConfig=@sortingConfig onSortClick=@onSortClick}}
          as |bt|
        >
          <bt.head />

          {{! If not transforming and no students to show, always only display the empty message }}
          {{#if (and this.transformItems.isIdle (not this.studentRows))}}
            <bt.spanner class="p-3 text-center italic">
              {{t "tables.fluencySprints.emptyTableMessage"}}
            </bt.spanner>
          {{else}}
            {{! If transforming, always show a loading spinner}}
            {{! When no items, show it normally. }}
            {{! If there are items, place it on top of the existing table body (absolute) }}
            {{#if this.transformItems.isRunning}}
              <bt.spanner
                class="table-box table-box-cell border-transparent
                  {{if this.studentRows 'absolute left-1/2 -translate-x-1/2 transform'}}"
              >
                <FidgetSpinnerWaveComponent @small={{true}} @centered={{true}} />
              </bt.spanner>
            {{/if}}
            <bt.body class={{if this.transformItems.isRunning "opacity-50"}} />
          {{/if}}
        </BasicTable>
        {{#if @exportable}}
          <BasicTable
            class="hidden"
            exportable={{true}}
            exportable-join-id="fs-student-results"
            @columns={{this.exportStudentColumns}}
            @rows={{this.unpaginatedItems}}
            @config={{hash sortingConfig=@sortingConfig}}
            as |bt|
          >
            <bt.head />
            {{#if this.unpaginatedItems}}
              <bt.body />
            {{/if}}
          </BasicTable>
        {{/if}}
      </section>

      <section data-test-lessons class="fixed-table-body">
        <StarLegend @showNotPassed={{true}} class="sticky top-0 left-0 block h-[52px] py-[10px] print:hidden" />
        <TabGroupComponent @tabType={{TAB_TYPE_KEYS.border}} class="text-sm" as |tabGroup|>
          {{! All tab }}
          <tabGroup.tab
            data-test-tab="all"
            @theme={{get this.categoryThemeMapping "all"}}
            @isActive={{not @category}}
            {{on "click" (fn this.onTabClick (hash category=undefined))}}
          >
            {{t "tables.fluencySprints.studentResults.categories.all"}}
          </tabGroup.tab>
          {{! Category tabs }}
          {{#each (array "addition" "subtraction" "multiplication" "division") as |categoryId|}}
            <tabGroup.tab
              data-test-tab={{categoryId}}
              @theme={{get this.categoryThemeMapping categoryId}}
              @isActive={{eq categoryId @category}}
              {{on "click" (fn this.onTabClick (hash category=categoryId))}}
            >
              {{t (concat "tables.fluencySprints.studentResults.categories." categoryId)}}
            </tabGroup.tab>
          {{/each}}
        </TabGroupComponent>

        <tab-content id="results-tab-content" class="block overflow-x-auto">
          <BasicTable @columns={{this.lessonColumns}} @rows={{this.lessonRows}} as |bt|>
            <bt.head as |h|>
              <h.row as |r|>
                <r.cell class={{@category}} as |meta|>
                  <div class="lessons-wrapper">
                    <span class="lesson-name">
                      {{meta.cellObject.value.name}}
                    </span>
                    <div class="lesson-id text-center {{meta.cellObject.value.category}}">
                      {{meta.cellObject.value.id}}
                    </div>
                  </div>
                </r.cell>
              </h.row>
            </bt.head>
            {{#if this.studentRows}}
              <bt.body />
            {{/if}}
          </BasicTable>
          {{#if @exportable}}
            <BasicTable
              class="hidden"
              exportable={{true}}
              exportable-join-with="fs-student-results"
              @columns={{this.lessonColumns}}
              @rows={{this.unpaginatedItems}}
              @config={{hash sortingConfig=@sortingConfig}}
              as |bt|
            >
              <bt.head as |h|>
                <h.row as |r|>
                  <r.cell as |meta|>
                    {{#if meta.cellObject.value}}
                      {{meta.cellObject.value.id}}:
                      {{meta.cellObject.value.name}}
                    {{/if}}
                  </r.cell>
                </h.row>
              </bt.head>
              {{#if this.unpaginatedItems}}
                <bt.body />
              {{/if}}
            </BasicTable>
          {{/if}}
        </tab-content>
      </section>

      <BasePageSelector
        class="clear-both mx-2 py-4"
        @pageCount={{this.pageCount}}
        @itemCount={{this.unpaginatedItems.length}}
      />
    </div>
  </template>
}

export default FluencySprintsStudentResults
